import React, { createContext, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUserContent,
  deleteUserContent,
  getUserContent,
} from "../api/userContent.api";
import { UserContentRow, CreateUserContentRow } from "../api/userContent.type";
import { useAuthContext } from "../containers/AuthContextWrapper";
import { useToastContext } from "../containers/ToastContextWrapper";
import { useUserContext } from "./UserContextWrapper";

interface UserContentContextType {
  savedIds: string[];
  toggleSaved: (id: string) => void;
  isSaved: (id: string) => boolean;
  isLoading: boolean;
}

const UserContentContext = createContext<UserContentContextType>({
  savedIds: [],
  toggleSaved: () => {},
  isSaved: () => false,
  isLoading: false,
});

export const UserContentContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { session } = useAuthContext();
  const { user } = useUserContext();
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();

  const { data: userContent = [], isLoading } = useQuery<UserContentRow[]>({
    queryKey: ["user-content"],
    queryFn: () => getUserContent(),
    enabled: !!session,
  });

  const { mutateAsync: add } = useMutation({
    mutationFn: createUserContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-content"] });
      showToast({
        title: "Saved",
        description: "Content saved successfully",
        type: "success",
        duration: 5000,
      });
    },
  });

  const { mutateAsync: remove } = useMutation({
    mutationFn: deleteUserContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-content"] });
      showToast({
        title: "Removed",
        description: "Content removed",
        type: "success",
        duration: 5000,
      });
    },
  });

  const savedIds = userContent.map((c) => c.content_id);

  const isSaved = (id: string) => savedIds.includes(id);

  const toggleSaved = async (id: string) => {
    const existing = userContent.find((c) => c.content_id === id);
    if (existing) {
      await remove(existing.id);
    } else {
      const payload: CreateUserContentRow = {
        content_id: id,
        user_id: user?.id || "",
      };
      await add(payload);
    }
  };

  return (
    <UserContentContext.Provider
      value={{ savedIds, toggleSaved, isSaved, isLoading }}
    >
      {children}
    </UserContentContext.Provider>
  );
};

export const useUserContent = () => useContext(UserContentContext);
